// Localização: ./src/pages/Pacientes/PacienteList.jsx

import React, { useState, useMemo, useEffect } from 'react';
import PacienteFormContent from '../../components/Pacientes/PacienteFormContent';
import PrimaryButton from '../../components/Common/PrimaryButton';
 
// Hooks
import { usePacienteList } from '../../hooks/usePacienteList'; 
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../context/SearchContext';
import { usePacientesPagamentoStatus } from '../../hooks/usePacientePagamentoStatus';
// Utils
import { getPacienteFotoUrl } from '../../utils/imageUtils';
// Ícones do Lucide
import { SquarePen, Trash2, Plus, Pill, ClipboardList, Calendar, MapPin, HandCoins, CheckCircle2, AlertCircle, Clock, Wallet } from 'lucide-react';

// Define a URL do avatar de fallback (deve corresponder ao imageUtils.js)
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png'; 

/**
 * Função utilitária para separar o primeiro nome e o sobrenome (mantida)
 * @param {string} nomeCompleto - Nome completo do paciente
 * @returns {{nome: string, sobrenome: string}}
 */
const splitName = (nomeCompleto) => {
    if (!nomeCompleto) return { nome: 'N/A', sobrenome: '' };
    const parts = nomeCompleto.trim().split(' ');
    const nome = parts[0] || '';
    const sobrenome = parts.slice(1).join(' ') || '';
    return { nome, sobrenome };
};

// Componente de Card do Paciente
const PacienteCard = ({ paciente, onEdit, onDelete, onSummary, onGoToPlan, pagamentoStatus, valorMensalidade }) => {
    const { nome, sobrenome } = splitName(paciente.nome_completo);
    const nomeCompleto = `${nome} ${sobrenome}`;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatCurrency = (valor) => {
        if (!valor || valor === 0) return null;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const handleCardClick = (e) => {
        // Evita navegação se o clique foi em um botão ou link
        if (e.target.closest('button')) {
            return;
        }
        onSummary(paciente);
    };

    // Configuração do badge de status de pagamento
    const getPagamentoBadge = () => {
        if (!pagamentoStatus) return null;

        const configs = {
            pago: {
                label: 'Pago',
                bgColor: 'bg-green-100',
                textColor: 'text-green-800',
                icon: CheckCircle2,
                borderColor: 'border-green-300'
            },
            atrasado: {
                label: 'Atrasado',
                bgColor: 'bg-red-100',
                textColor: 'text-red-800',
                icon: AlertCircle,
                borderColor: 'border-red-300'
            },
            pendente: {
                label: 'Pendente',
                bgColor: 'bg-yellow-100',
                textColor: 'text-yellow-800',
                icon: Clock,
                borderColor: 'border-yellow-300'
            }
        };

        const config = configs[pagamentoStatus];
        if (!config) return null;

        const Icon = config.icon;

        return (
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-semibold border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
                <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>{config.label}</span>
            </div>
        );
    };

    return (
        <div 
            className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group cursor-pointer"
            onClick={handleCardClick}
        >
            {/* Header do Card com Foto */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100 p-2 sm:p-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                    {/* Foto do Paciente */}
                    <div className="flex-shrink-0">
                        <div className="relative">
                            <img 
                                className="h-12 w-12 sm:h-16 sm:w-16 rounded-full object-cover border-2 sm:border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-300" 
                                src={getPacienteFotoUrl(paciente.fotoURL)} 
                                alt={`Foto de ${nomeCompleto}`} 
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = FALLBACK_AVATAR; 
                                }}
                            />
                            {/* Status Badge */}
                            <div className={`absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 h-3.5 w-3.5 sm:h-5 sm:w-5 rounded-full border-2 border-white ${
                                paciente.status === 'Ativo' ? 'bg-green-500' : 'bg-red-500'
                            }`} title={paciente.status}></div>
                        </div>
                    </div>
                    
                    {/* Nome e Condição */}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-lg font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {nomeCompleto}
                        </h3>
                        {paciente.condicao_medica && (
                            <p className="text-xs text-gray-600 truncate mt-0.5 sm:mt-1">
                                {paciente.condicao_medica}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Corpo do Card */}
            <div className="p-2 sm:p-4 space-y-2 sm:space-y-3">
                {/* Informações */}
                <div className="space-y-1.5 sm:space-y-2">
                    {/* Data de Nascimento */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">
                            <span className="font-semibold">Nasc.:</span> {formatDate(paciente.data_nascimento)}
                        </span>
                    </div>

                    {/* Endereço */}
                    {paciente.endereco && (
                        <div className="flex items-start text-xs sm:text-sm text-gray-600">
                            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="truncate line-clamp-2">
                                {paciente.endereco}
                            </span>
                        </div>
                    )}

                    {/* Mensalidade */}
                    {valorMensalidade && (
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                            <Wallet className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                            <span className="truncate">
                                <span className="font-semibold">Mensalidade:</span> {formatCurrency(valorMensalidade)}
                            </span>
                        </div>
                    )}

                    {/* Status Badge e Status de Pagamento */}
                    <div className="flex items-center justify-between pt-0.5 sm:pt-1 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-0.5 sm:px-3 sm:py-1 inline-flex text-xs font-semibold rounded-full ${
                                paciente.status === 'Ativo' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                            }`}>
                                {paciente.status}
                            </span>
                        </div>
                        {getPagamentoBadge()}
                    </div>
                </div>

                {/* Ações */}
                <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Botão Prontuário */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onSummary(paciente);
                            }}
                            className="p-1.5 sm:p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                            title="Ir para Prontuário"
                        >
                            <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        
                        {/* Botão Plano Médico */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onGoToPlan(paciente.id);
                            }}
                            className="p-1.5 sm:p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors duration-200"
                            title="Gerenciar Plano Médico"
                        >
                            <Pill className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>

                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Botão Editar */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(paciente);
                            }}
                            className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                            title="Editar"
                        >
                            <SquarePen className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        
                        {/* Botão Excluir */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(paciente.id);
                            }}
                            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="Excluir/Desativar"
                        >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente Principal
function PacienteList() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPaciente, setEditingPaciente] = useState(null);
    const [filtroPagamento, setFiltroPagamento] = useState(null); // null, 'pago', 'atrasado', 'pendente'
    const { searchTerm, setSearchTerm, setSearchPlaceholder, setOnSearchChange } = useSearch();

    const { 
        pacientes = [], 
        isLoading, 
        error, 
        fetchPacientes, 
        deletePaciente, 
        refreshing 
    } = usePacienteList();

    const navigate = useNavigate();

    // Buscar status de pagamento de todos os pacientes
    const pacienteIds = useMemo(() => pacientes.map(p => p.id), [pacientes]);
    const { statusMap, mensalidadeMap, isLoading: isLoadingPagamento } = usePacientesPagamentoStatus(pacienteIds);

    // Configurar o contexto de busca para esta página
    useEffect(() => {
        setSearchPlaceholder('Buscar por nome, endereço ou status...');
        setOnSearchChange((value) => {
            setSearchTerm(value);
        });
        // Cleanup ao sair da página
        return () => {
            setOnSearchChange(null);
        };
    }, [setSearchPlaceholder, setOnSearchChange, setSearchTerm]);

    const filteredPacientes = useMemo(() => {
        // Primeiro, filtra pacientes excluídos (oculta pacientes com status 'excluído' ou 'Excluído')
        const pacientesAtivos = pacientes.filter(paciente => {
            const statusLower = paciente.status?.toLowerCase() || '';
            return statusLower !== 'excluído' && statusLower !== 'excluido';
        });

        // Aplicar filtro de busca se houver termo de pesquisa
        let pacientesFiltrados = pacientesAtivos;
        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            pacientesFiltrados = pacientesAtivos.filter(paciente => 
                (paciente.nome_completo && paciente.nome_completo.toLowerCase().includes(lowerCaseSearchTerm)) ||
                (paciente.endereco && paciente.endereco.toLowerCase().includes(lowerCaseSearchTerm)) ||
                (paciente.status && paciente.status.toLowerCase().includes(lowerCaseSearchTerm))
            );
        }

        // Aplicar filtro de status de pagamento
        if (filtroPagamento) {
            pacientesFiltrados = pacientesFiltrados.filter(paciente => {
                const statusPagamento = statusMap[paciente.id];
                return statusPagamento === filtroPagamento;
            });
        }

        return pacientesFiltrados;
    }, [pacientes, searchTerm, filtroPagamento, statusMap]);

    const handleGoToProntuario = (paciente) => {
        navigate(`/pacientes/${paciente.id}/prontuario`);
    };
    
    const handleGoToPlan = (pacienteId) => {
        navigate(`/pacientes/${pacienteId}/planos`);
    };
    
    const handleOpenModal = (paciente = null) => {
        setEditingPaciente(paciente);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPaciente(null);
        fetchPacientes(); 
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja desativar este paciente? Esta ação pode ser desfeita na edição.")) {
            await deletePaciente(id);
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen"> 
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col gap-4 mb-4">
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                                    Gerenciamento de Pacientes
                                </span>
                            </h1>
                            <p className="text-sm text-gray-500 font-medium">
                                Gerencie e acompanhe todos os pacientes da sua organização
                            </p>
                        </div>
                        
                        {/* Filtro de Status de Pagamento e Botão Novo Paciente */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                            {/* Filtro de Status de Pagamento */}
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap flex-1">
                                <div className="flex items-center gap-2">
                                    <HandCoins className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Status Pagamento:</span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        onClick={() => setFiltroPagamento(null)}
                                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 border ${
                                            filtroPagamento === null
                                                ? 'bg-emerald-100 text-emerald-700 border-emerald-300 shadow-sm font-semibold'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                        }`}
                                    >
                                        Todos
                                    </button>
                                    <button
                                        onClick={() => setFiltroPagamento('pago')}
                                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 border flex items-center gap-1.5 ${
                                            filtroPagamento === 'pago'
                                                ? 'bg-green-100 text-green-700 border-green-300 shadow-sm font-semibold'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50 hover:border-green-300'
                                        }`}
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Pago
                                    </button>
                                    <button
                                        onClick={() => setFiltroPagamento('pendente')}
                                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 border flex items-center gap-1.5 ${
                                            filtroPagamento === 'pendente'
                                                ? 'bg-yellow-100 text-yellow-700 border-yellow-300 shadow-sm font-semibold'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-yellow-50 hover:border-yellow-300'
                                        }`}
                                    >
                                        <Clock className="w-3.5 h-3.5" />
                                        Pendente
                                    </button>
                                    <button
                                        onClick={() => setFiltroPagamento('atrasado')}
                                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 border flex items-center gap-1.5 ${
                                            filtroPagamento === 'atrasado'
                                                ? 'bg-red-100 text-red-700 border-red-300 shadow-sm font-semibold'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-red-50 hover:border-red-300'
                                        }`}
                                    >
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        Atrasado
                                    </button>
                                </div>
                            </div>

                            {/* Botão Novo Paciente */}
                            <PrimaryButton
                                onClick={() => handleOpenModal(null)}
                                className="w-full sm:w-auto flex-shrink-0"
                                icon={<Plus className="w-5 h-5 mr-1" />}
                            >
                                Novo Paciente
                            </PrimaryButton>
                        </div>
                    </div>
                </div>

                {/* Conteúdo Principal */}
                {isLoading || refreshing ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                        <p className="text-emerald-600 font-semibold text-lg">Carregando pacientes...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-md" role="alert">
                        <strong className="font-bold">Erro de Comunicação:</strong>
                        <span className="block sm:inline"> {error}</span>
                        <p className="text-sm mt-1">Verifique se o seu servidor Node (Backend) está rodando.</p>
                    </div>
                ) : filteredPacientes.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                        <p className="text-gray-500 text-lg">Nenhum paciente encontrado.</p>
                    </div>
                ) : (
                    /* Grid de Cards - Responsivo: 2 colunas no mobile, 4 colunas no desktop/tablet */
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 md:gap-6">
                        {filteredPacientes.map((paciente) => (
                            <PacienteCard
                                key={paciente.id}
                                paciente={paciente}
                                onEdit={handleOpenModal}
                                onDelete={handleDelete}
                                onSummary={handleGoToProntuario}
                                onGoToPlan={handleGoToPlan}
                                pagamentoStatus={statusMap[paciente.id]}
                                valorMensalidade={mensalidadeMap[paciente.id]?.valor}
                            />
                        ))}
                    </div>
                )}

                {/* Modal de Formulário */}
                <PacienteFormContent 
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    pacienteData={editingPaciente}
                />
            </div>
        </div>
    );
}

export default PacienteList;
